// components/SearchBar.js
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  InputBase,
  IconButton,
  useTheme,
  Drawer,
  Autocomplete,
  Chip,
  TextField,
  Badge,
  Tooltip,
  Typography,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import { useSearch } from "../hooks/useSearch";
import { PropertyFilters } from "./PropertyFilters";

import { useGetSearchSuggestionsQuery } from "../features/search/searchApiSlice";
import { useLazySearchPropertiesQuery } from "../features/search/searchApiSlice";

import {
  setSearchResults,
  setLoading,
  setError,
  // addToRecentSearches,
} from "../features/search/searchSlice";
import { Apartment, CurrencyRupee, LocationOn } from "@mui/icons-material";

// Constants
const propertyTypes = [
  "Apartment",
  "Villa",
  "Townhouse",
  "Penthouse",
  "Studio",
];
const amenitiesList = [
  "Wifi",
  "Laundry",
  // "AC",
  "Parking",
  // "Pool",
  "Gym",
  // "Pet Friendly",
  // "Furnished",
];
const genderLabels = {
  male: "Men",
  female: "Women",
  // coed: "Unisex",
};
const tenantTypeLabels = {
  students: "Students",
  professional: "Professionals",
  both: "Students & Professionals",
};
const sharingTypeLabels = {
  1: "Private Room",
  2: "Double Sharing",
  3: "Triple Sharing",
  4: "4+ Sharing",
};
const bathroomTypeLabels = {
  attached: "Attached",
  common: "Common",
};

function SearchBar() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // State to store selected suggestion data
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);

  //   const [searchPropertiesApi, { isLoading: apiLoading }] =
  //     useSearchPropertiesMutation();
  const [
    triggerSearch,
    {
      // data: apiResponse,
      isLoading: apiLoading,
      // error: apiError,
    },
  ] = useLazySearchPropertiesQuery();

  const {
    searchQuery,
    filters,
    filtersOpen,
    recentSearches,
    // loading,
    handleSearch,
    // activeFilterCount,
    handleFilterChange,
    handleInputChange,
    handlePriceChange,
    handleNumberChange,
    handleAmenityToggle,
    handlePropertyTypeChange,
    handleClearAll,
    handleQuickSearch,
    toggleFilters,
    closeFilters,
  } = useSearch();

  // Handle "Enter" key press
  const onKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch(e);
    }
  };

  // Get search suggestions
  const { data: suggestions = [], isFetching: loadingSuggestions } =
    useGetSearchSuggestionsQuery(searchQuery, {
      skip: !searchQuery || searchQuery.length < 2,
    });

  // Calculate active filter count
  const activeFilterCount = React.useMemo(() => {
    let count = 0;

    // Price filters (check if not default values)
    if (filters.minPrice !== 1000 || filters.maxPrice !== 10000) {
      count++;
    }

    // // Bedrooms filter
    // if (filters.bedrooms > 0) {
    //   count++;
    // }

    // // Bathrooms filter
    // if (filters.bathrooms > 0) {
    //   count++;
    // }

    // Gender filter
    if (filters.gender) {
      count++;
    }

    // Tenant type filter
    if (filters.tenantType) {
      count++;
    }

    // Sharing type filter
    if (filters.sharingType) {
      count++;
    }

    // Bathroom type filter
    if (filters.bathroomType) {
      count++;
    }

    // // Property type filter
    // if (filters.propertyType) {
    //   count++;
    // }

    // Amenities filter
    if (filters.amenities.length > 0) {
      // count++;
      count += filters.amenities.length;
    }

    return count;
  }, [filters]);

  // Add this function to get filter details
  const getActiveFilterDetails = (filters) => {
    const details = [];

    if (filters.minPrice !== 1000 || filters.maxPrice !== 10000) {
      // details.push(`Price: AED ${filters.minPrice} - ${filters.maxPrice}`);
      details.push(
        <Box
          component="span"
          sx={{ display: "inline-flex", alignItems: "center" }}
        >
          Price:{" "}
          <CurrencyRupee
            sx={{ fontSize: 13, ml: 0.5, transform: "translateY(-1px)" }}
          />{" "}
          {filters.minPrice} - {filters.maxPrice}
        </Box>,
      );
    }

    // if (filters.bedrooms > 0) {
    //   details.push(`Bedrooms: ${filters.bedrooms}+`);
    // }

    // if (filters.bathrooms > 0) {
    //   details.push(`Bathrooms: ${filters.bathrooms}+`);
    // }

    if (filters.gender) {
      // details.push(`Gender: ${filters.gender}`);
      const label = genderLabels[filters.gender] || filters.gender;
      details.push(`Gender: ${label}`);
    }

    if (filters.tenantType) {
      // details.push(`Tenant: ${filters.tenantType}`);
      const label = tenantTypeLabels[filters.tenantType] || filters.tenantType;
      details.push(`Tenant: ${label}`);
    }

    if (filters.sharingType) {
      // details.push(`Room Sharing: ${filters.sharingType}`);
      const label =
        sharingTypeLabels[filters.sharingType] || filters.sharingType;
      details.push(`Room Sharing: ${label}`);
    }

    if (filters.bathroomType) {
      // details.push(`Bathroom Type: ${filters.bathroomType}`);
      const label =
        bathroomTypeLabels[filters.bathroomType] || filters.bathroomType;
      details.push(`Bathroom Type: ${label}`);
    }

    // if (filters.propertyType) {
    //   details.push(`Type: ${filters.propertyType}`);
    // }

    if (filters.amenities.length > 0) {
      details.push(`Amenities: ${filters.amenities.length}`);
    }

    return details;
  };

  //   const handleSubmit = (e) => {
  //     e.preventDefault();

  //     // Set loading state
  //     dispatch(setLoading(true));
  //     dispatch(setError(null));

  //     try {
  //       let searchParams = {};

  //       // If a suggestion is selected, use its data
  //       if (selectedSuggestion) {
  //         searchParams = {
  //           title: selectedSuggestion.title,
  //           location: selectedSuggestion.location,
  //           suggestionId: selectedSuggestion.id,
  //           ...filters, // Include current filters
  //         };

  //         // Add to recent searches
  //         dispatch(
  //           addToRecentSearches({
  //             query: selectedSuggestion.title,
  //             location: selectedSuggestion.location,
  //             ...filters,
  //           })
  //         );
  //       } else {
  //         // Normal text search
  //         searchParams = {
  //           query: searchQuery,
  //           ...filters,
  //         };

  //         // Add to recent searches
  //         if (searchQuery) {
  //           dispatch(
  //             addToRecentSearches({
  //               query: searchQuery,
  //               ...filters,
  //             })
  //           );
  //         }
  //       }

  //       console.log("Search params:", searchParams);

  //       // Call the API using the lazy query trigger
  //       const result = triggerSearch(searchParams, false); // 'false' prevents refetch on arg change

  //       if (result.isError) {
  //         throw new Error(result.error?.data?.message || "Search failed");
  //       }

  //       // Call the API
  //       //   const response = searchPropertiesApi(searchParams).unwrap();
  //       console.log("API Response:", result.data);

  //       // Dispatch results to Redux store
  //       dispatch(
  //         setSearchResults({
  //           results: result.data?.data || result.data || [],
  //           total: result.data?.total || result.data?.count || 0,
  //           page: result.data?.page || 1,
  //           totalPages: result.data?.totalPages || 1,
  //         })
  //       );

  //       // Navigate to search results page
  //       // This depends on your routing setup
  //       // navigate('/search');
  //     } catch (error) {
  //       console.error("Search error:", error);
  //       dispatch(setError(error.message || "Search failed"));

  //       // Clear any previous results
  //       dispatch(
  //         setSearchResults({
  //           results: [],
  //           total: 0,
  //           page: 1,
  //           totalPages: 1,
  //         })
  //       );
  //     } finally {
  //       dispatch(setLoading(false));
  //       setSelectedSuggestion(null);
  //     }
  //   };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();

  //   // Set loading state
  //   dispatch(setLoading(true));
  //   dispatch(setError(null));

  //   try {
  //     let searchParams = {};

  //     // Build searchParams (your existing code)
  //     if (selectedSuggestion) {
  //       searchParams = {
  //         title: selectedSuggestion.title,
  //         location: selectedSuggestion.location,
  //         suggestionId: selectedSuggestion.id,
  //         ...filters,
  //       };
  //       dispatch(
  //         addToRecentSearches({
  //           query: selectedSuggestion.title,
  //           location: selectedSuggestion.location,
  //           ...filters,
  //         })
  //       );
  //     } else {
  //       searchParams = {
  //         query: searchQuery,
  //         ...filters,
  //       };
  //       if (searchQuery) {
  //         dispatch(
  //           addToRecentSearches({
  //             query: searchQuery,
  //             ...filters,
  //           })
  //         );
  //       }
  //     }

  //     // console.log("Search params:", searchParams);

  //     // 🔥 CRITICAL FIX: Properly handle the async API call
  //     // Method 1: Using the returned promise
  //     const response = await triggerSearch(searchParams).unwrap();
  //     // console.log("API Response:", response);

  //     // Method 2: Or use the destructured apiResponse from the hook
  //     // await triggerSearch(searchParams);
  //     // if (apiError) throw new Error(apiError.data?.message || "Search failed");
  //     // const response = apiResponse;

  //     // Dispatch results to Redux store
  //     dispatch(
  //       setSearchResults({
  //         results: response?.data || response || [],
  //         total: response?.total || response?.count || 0,
  //         page: response?.page || 1,
  //         totalPages: response?.totalPages || 1,
  //       })
  //     );

  //     // 🔥 ADD: Navigate to search results page
  //     //   navigate("/search"); // Make sure to import useNavigate
  //   } catch (error) {
  //     console.error("Search error:", error);
  //     dispatch(
  //       setError(error?.data?.message || error.message || "Search failed")
  //     );

  //     dispatch(
  //       setSearchResults({
  //         results: [],
  //         total: 0,
  //         page: 1,
  //         totalPages: 1,
  //       })
  //     );
  //   } finally {
  //     dispatch(setLoading(false));
  //     setSelectedSuggestion(null);
  //   }
  // };

  // // Handle selection from suggestions
  // const handleSuggestionSelect = (event, newValue) => {
  //   if (newValue) {
  //     if (typeof newValue === "string") {
  //       // User typed something
  //       handleInputChange(newValue);
  //       setSelectedSuggestion(null); // Clear selected suggestion
  //     } else if (newValue.label) {
  //       // User selected a suggestion object
  //       setSelectedSuggestion(newValue);
  //       // Keep the label displayed in the input
  //       handleInputChange(newValue.label);

  //       // You have multiple options here:

  //       // Option A: Search by ID (most precise)
  //       // navigate(`/properties/${newValue.id}`);

  //       // Option B: Search by title only
  //       handleInputChange(newValue.title);

  //       // Option C: Search by title AND location
  //       // handleSearch({
  //       //   ...allSearchParams,
  //       //   query: newValue.title,
  //       //   location: newValue.location
  //       // });

  //       // Option D: Immediate search with title
  //       // handleInputChange(newValue.title);
  //       // Optionally trigger search immediately
  //       // handleSearch({ ...allSearchParams, query: newValue.title });
  //     }
  //   } else {
  //     // Cleared the input
  //     handleInputChange("");
  //     setSelectedSuggestion(null);
  //   }
  // };

  const handleSuggestionSelect = (event, newValue) => {
    // 1. Handle Clear
    // if (!newValue) {
    //   handleInputChange("");
    //   return;
    // }

    // 1. Handle "Clear" (X button clicked)
    if (newValue === null) {
      handleInputChange(""); // Reset Redux state
      // navigate("/search"); // Optional: Reset URL
      return;
    }

    // 1. Determine the query string
    let query = "";
    let id = "";
    let type = ""; // "location" or "property"

    if (typeof newValue === "string") {
      query = newValue;
    }
    // else if (newValue && newValue.title) {
    //   query = newValue.title;
    //   suggestionId = newValue.id || newValue._id;
    // }
    else if (typeof newValue === "object" && newValue !== null) {
      // User selected an object from the list
      // CHECK for 'label' OR 'title' to be safe
      query = newValue.label || newValue.title || "";
      id = newValue.id || newValue._id || "";
      type = newValue.type || "property"; // Default to property if type missing
    } else {
      return; // clear or invalid
    }

    // 2. Update Redux State immediately so UI reflects selection
    handleInputChange(query);

    // 3. Build URL & Navigate (Bypass handleSearch to avoid Redux async lag)
    const params = new URLSearchParams();

    // Logic: If user typed text, send 'q'.
    // If user selected a suggestion, send 'q' (for UI) AND the specific ID.
    if (query) params.set("q", query);

    if (id) {
      if (type === "location" || type === "city") {
        params.set("cityId", id); // It's a city
      } else {
        params.set("suggestionId", id); // It's a specific building/property
      }
    }

    // params.set("q", query);
    // if (suggestionId) params.set("suggestionId", suggestionId);
    // console.log(suggestionId);

    // Preserve existing filters
    if (filters.minPrice > 0) params.set("minPrice", filters.minPrice);
    if (filters.maxPrice < 1000000) params.set("maxPrice", filters.maxPrice);
    // if (filters.bedrooms > 0) params.set("bedrooms", filters.bedrooms);
    // if (filters.propertyType) params.set("propertyType", filters.propertyType);
    if (filters.gender) params.set("gender", filters.gender);
    if (filters.tenantType) params.set("tenantType", filters.tenantType);
    if (filters.sharingType) params.set("sharingType", filters.sharingType);
    if (filters.bathroomType) params.set("bathroomType", filters.bathroomType);
    if (filters.amenities && filters.amenities.length > 0) {
      params.set("amenities", filters.amenities.join(","));
    }
    // console.log(199, params);

    navigate(`/search?${params.toString()}`);
  };

  const handleInputChangeWrapper = (newValue) => {
    // If user is typing (not selecting from dropdown), clear selected suggestion
    if (typeof newValue === "string") {
      handleInputChange(newValue);
      // Only clear if the typed value doesn't match any suggestion label
      if (!suggestions.some((suggestion) => suggestion.label === newValue)) {
        setSelectedSuggestion(null);
      }
    }
  };

  //   const handleApplyFilters = () => {
  //     handleSearch();
  //     closeFilters();
  //   };

  const handleApplyFilters = async () => {
    // e.preventDefault();

    // Set loading state
    dispatch(setLoading(true));
    dispatch(setError(null));

    try {
      // const searchParams = selectedSuggestion
      //   ? {
      //       title: selectedSuggestion.title,
      //       location: selectedSuggestion.location,
      //       suggestionId: selectedSuggestion.id,
      //       ...filters,
      //     }
      //   : {
      //       query: searchQuery,
      //       ...filters,
      //     };

      const searchParams = {
        query: searchQuery,
        ...filters,
      };

      //   console.log("Filter search params:", searchParams);

      // Call the API
      //   const response = searchPropertiesApi(searchParams).unwrap();

      // Call the API using the lazy query trigger
      //   const result = await triggerSearch(searchParams, false); // 'false' prevents refetch on arg change
      //   const result = await triggerSearch(searchParams).unwrap();

      const response = await triggerSearch(searchParams).unwrap();

      // console.log("API Response:", response);

      //   if (result.isError) {
      //     throw new Error(result.error?.data?.message || "Search failed");
      //   }

      //   const response = result.data;

      // Call the API
      //   const response = searchPropertiesApi(searchParams).unwrap();
      //   console.log("API Response:", result.data);

      // Dispatch results to Redux store
      dispatch(
        // setSearchResults({
        //   results: response.data || response,
        //   total:
        //     response.total ||
        //     response.count ||
        //     (response.data ? response.data.length : response.length),
        //   page: response.page || 1,
        //   totalPages: response.totalPages || 1,
        // })
        setSearchResults({
          results: response.data?.data || response || [],
          total: response.data?.total || response?.count || 0,
          page: response.data?.page || 1,
          totalPages: response.data?.totalPages || 1,
        }),
      );

      // Navigate to search page with filters
      const params = new URLSearchParams();
      if (searchQuery) params.set("q", searchQuery);
      if (filters.minPrice > 0) params.set("minPrice", filters.minPrice);
      if (filters.maxPrice < 1000000) params.set("maxPrice", filters.maxPrice);
      if (filters.bedrooms > 0) params.set("bedrooms", filters.bedrooms);
      if (filters.bathrooms > 0) params.set("bathrooms", filters.bathrooms);
      if (filters.propertyType)
        params.set("propertyType", filters.propertyType);
      if (filters.amenities && filters.amenities.length > 0) {
        params.set("amenities", filters.amenities.join(","));
      }

      navigate(`/search?${params.toString()}`);

      closeFilters();
    } catch (error) {
      console.error("Filter search error:", error);
      dispatch(
        setError(error.data?.message || error.message || "Search failed"),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Convert recentSearches array to array of strings for Chip display
  const recentSearchQueries =
    recentSearches?.map((search) => search.query) || [];

  return (
    <>
      <Box
        sx={{
          flexGrow: 1,
          mx: { xs: 1, md: 3 },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Paper
          component="form"
          // onSubmit={handleSubmit}
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch(e);
          }}
          sx={{
            p: "2px 4px",
            display: "flex",
            alignItems: "center",
            width: { xs: "100%", md: "500px", lg: "600px" },
            maxWidth: "100%",
            borderRadius: 5,
            boxShadow: theme.shadows[1],
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* <IconButton sx={{ p: "10px" }} aria-label="menu"> */}
          {/* ... */}
          {/* </IconButton> */}
          {/* Single Autocomplete Input (replaces both InputBase and Autocomplete) */}
          <Autocomplete
            freeSolo
            fullWidth
            // 1. CONTROLLED INPUT: Bind inputValue to Redux state
            inputValue={searchQuery || ""}
            // 2. SERVER-SIDE FILTERING: Disable client-side filtering
            filterOptions={(x) => x}
            // 3. OPTIONS: From API
            options={suggestions}
            loading={loadingSuggestions}
            // 4. HANDLE TYPING
            // onInputChange={(event, newInputValue) => {
            //   // Prevent clearing on blur/focus cycles if needed, or just update
            //   if (event && event.type === "change") {
            //     handleInputChange(newInputValue);
            //   } else if (event === null) {
            //     // Handle clear button
            //     handleInputChange(newInputValue);
            //   }
            // }}
            onInputChange={(event, newInputValue) => {
              handleInputChange(newInputValue);
            }}
            // onInputChange={(event, newInputValue, reason) => {
            //   // Update state if user types OR if they click the clear button
            //   if (reason === "input" || reason === "clear") {
            //     handleInputChange(newInputValue);
            //   }
            // }}
            // 5. HANDLE SELECTION
            onChange={handleSuggestionSelect}
            // onChange={(event, newValue) => {
            //   if (!newValue) return;

            //   if (newValue.type === "location") {
            //     // If they click a city, search by location
            //     handleInputChange(newValue.label);
            //     navigate(`/search?city=${newValue.label}`);
            //   } else {
            //     // If they click a building, go straight to that property
            //     navigate(`/property/${newValue.id}`);
            //   }
            // }}
            // options={recentSearches || []}
            // value={searchQuery}
            // onInputChange={(event, newInputValue) => {
            //   handleInputChange(newInputValue);
            // }}
            // disableClearable
            // options={suggestions}
            // inputValue={inputValue}
            // value={searchQuery}
            // onChange={(event, newValue) => {
            //   if (typeof newValue === "string") {
            //     handleInputChange(newValue);
            //   } else if (newValue) {
            //     handleInputChange(newValue);
            //   }
            // }}
            // onChange={handleSuggestionSelect} // Updated handler
            // onChange={(event, newValue) => {
            //   if (newValue) {
            //     if (typeof newValue === "string") {
            //       handleInputChange(newValue);
            //       setSelectedSuggestion(null);
            //     } else if (newValue.label) {
            //       setSelectedSuggestion(newValue);
            //       handleInputChange(newValue.label);
            //     }
            //   } else {
            //     handleInputChange("");
            //     setSelectedSuggestion(null);
            //   }
            // }}
            // onInputChange={(event, newValue) => {
            //   handleInputChange(newValue);
            // }}
            // onInputChange={handleInputChangeWrapper}
            // onInputChange={(event, newValue) => {
            //   // Only update input for typing, not selection
            //   if (event && event.type === "change") {
            //     handleInputChangeWrapper(newValue);
            //   }
            // }}
            // onInputChange={(event, newValue) => {
            //   if (event && event.type === "change") {
            //     handleInputChange(newValue);
            //     if (
            //       !suggestions.some(
            //         (suggestion) => suggestion.label === newValue
            //       )
            //     ) {
            //       setSelectedSuggestion(null);
            //     }
            //   }
            // }}
            getOptionLabel={(option) => {
              if (typeof option === "string") {
                return option;
              }
              // return option.label || option;
              return (
                option.label ||
                option.title ||
                // option.name ||
                // option.query ||
                ""
              );
            }}
            renderInput={(params) => {
              // Filter out InputLabelProps to prevent the warning
              const { InputLabelProps, ...restParams } = params;
              return (
                <TextField
                  {...restParams}
                  variant="standard"
                  // placeholder="Search properties, locations, or keywords..."
                  // placeholder="Search City, Building..."
                  placeholder="Search area, PG name or city"
                  onKeyDown={onKeyPress} // Listen for Enter
                  //   InputProps={{
                  //     ...restParams.InputProps,
                  //     disableUnderline: true,
                  //     sx: { ml: 2, flex: 1 },
                  //   }}
                  slotProps={{
                    input: {
                      ...restParams.InputProps,
                      disableUnderline: true,
                      sx: { ml: 2, flex: 1 },
                      //   type: "search",
                    },
                  }}
                  // disabled={loading}
                />
              );
            }}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <Box
                  component="li"
                  key={key}
                  {...optionProps}
                  sx={{ px: 2, py: 1 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Box sx={{ mr: 2, color: "text.secondary" }}>
                      {option.type === "location" ? (
                        <LocationOn />
                      ) : (
                        <Apartment />
                      )}
                    </Box>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {option.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.subLabel}
                      </Typography>
                    </Box>
                    <Chip
                      label={option.type}
                      size="small"
                      sx={{ ml: "auto", fontSize: "0.65rem", height: 20 }}
                    />
                  </Box>
                </Box>
              );
            }}
            // renderOption={(props, option) => {
            //   // Extract key from props and pass it directly
            //   const { key, ...otherProps } = props;
            //   return (
            //     <li key={key || option.id || option.title} {...otherProps}>
            //       <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            //         <SearchIcon fontSize="small" />
            //         <Typography variant="body2">
            //           {option.title || option.label || option}
            //         </Typography>
            //         {option.type && (
            //           <Chip
            //             label={option.type}
            //             size="small"
            //             sx={{ ml: "auto", height: 20, fontSize: "0.6rem" }}
            //           />
            //         )}
            //         {/* {option} */}
            //         {/* {option.label || option} */}
            //       </Box>
            //     </li>
            //   );
            // }}
            sx={{ flex: 1 }}
            // disabled={loading}
          />

          <IconButton
            type="submit"
            sx={{
              ml: "20px",
              p: "8px",
              bgcolor: theme.palette.primary.main,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 5,
              color: "white",
              borderColor: theme.palette.primary.main,
              "&:hover": {
                bgcolor: theme.palette.primary.dark,
              },
            }}
            aria-label="search"
            disabled={apiLoading}
            // onClick={handleSearch}
          >
            <SearchIcon />
          </IconButton>
        </Paper>

        {/* // Then update the Badge with Tooltip: */}
        {/* <Tooltip
          title={
            activeFilterCount > 0 ? (
              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold", mb: 0.5 }}
                >
                  Active Filters ({activeFilterCount}):
                </Typography>
                {getActiveFilterDetails(filters).map((detail, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{ fontSize: "0.8rem" }}
                  >
                    • {detail}
                  </Typography>
                ))}
              </Box>
            ) : (
              "No filters applied"
            )
          }
          arrow
        > */}
        {/* Filter Button with Badge */}
        {/* <Badge
            badgeContent={activeFilterCount}
            color="primary"
            sx={{ mx: 1 }}
            invisible={activeFilterCount === 0}
          >
            <IconButton
              sx={{
                p: "8px",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 5,
                color:
                  activeFilterCount > 0
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                borderColor:
                  activeFilterCount > 0
                    ? theme.palette.primary.main
                    : theme.palette.divider,
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  bgcolor: "rgba(0, 0, 0, 0.04)",
                },
              }}
              onClick={toggleFilters}
              aria-label="open filters"
            >
              <TuneIcon />
            </IconButton>
          </Badge>
        </Tooltip> */}

        {/* Filter Button */}
        <Tooltip
          title={
            activeFilterCount > 0 ? (
              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold", mb: 0.5 }}
                >
                  Active Filters ({activeFilterCount}):
                </Typography>
                {getActiveFilterDetails(filters).map((detail, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{ fontSize: "0.8rem" }}
                  >
                    • {detail}
                  </Typography>
                ))}
              </Box>
            ) : (
              "No filters applied"
            )
          }
          arrow
        >
          <IconButton
            sx={{
              p: "8px",
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 5,
              mx: 1,
              color: theme.palette.text.secondary,
              "&:hover": {
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                bgcolor: "rgba(0, 0, 0, 0.04)",
              },
            }}
            onClick={toggleFilters}
            //   onClick={() => setFiltersOpen(true)}
            aria-label="open filters"
          >
            {/* WRAP ICON IN BADGE */}
            <Badge
              badgeContent={activeFilterCount}
              color="primary"
              sx={{
                "& .MuiBadge-badge": {
                  //   bgcolor: theme.palette.text.secondary,
                  right: -5,
                  top: -5,
                },
              }}
            >
              <TuneIcon />
            </Badge>
          </IconButton>
        </Tooltip>
      </Box>

      {/* Recent Searches */}
      {/* {recentSearchQueries.length > 0 && (
        <Box
          sx={{
            mt: 2,
            display: "flex",
            gap: 1,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <Chip
            icon={<HistoryIcon />}
            label="Recent:"
            variant="outlined"
            size="small"
          />
          {recentSearchQueries.map((query, index) => (
            <Chip
              key={index}
              label={query}
              size="small"
              clickable
              onClick={() => handleQuickSearch(query)}
              onDelete={() => {
                console.log("Delete recent search:", query);
              }}
            />
          ))}
        </Box>
      )} */}

      {/* Filters Drawer */}
      <Drawer
        anchor="right"
        open={filtersOpen}
        onClose={closeFilters}
        PaperProps={{ sx: { width: { xs: "100%", sm: 450 }, p: 3 } }}
      >
        <PropertyFilters
          filters={filters}
          // propertyTypes={propertyTypes}
          amenitiesList={amenitiesList}
          onFilterChange={handleFilterChange}
          onPriceChange={handlePriceChange}
          onNumberChange={handleNumberChange}
          onAmenityToggle={handleAmenityToggle}
          onPropertyTypeChange={handlePropertyTypeChange}
          onClearAll={handleClearAll}
          //   onClearAll={resetSearch}
          // onApplyFilters={handleApplyFilters}
          onApplyFilters={() => {
            // Trigger Search Logic Manually from Filter Apply
            const e = { preventDefault: () => {} };
            handleSearch(e);
          }}
          onClose={closeFilters}
          loading={apiLoading}
        />
      </Drawer>
    </>
  );
}

export default SearchBar;
